import serialize from 'form-serialize';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAppContext } from '@prisjakt/ui-app-context';

import CurrencySettings from '@prisjakt/ui-currency/src/market-settings';
import T from '@prisjakt/ui-translate';
import { ButtonIcon, ButtonText } from '@prisjakt/ui/button/variants';
import { CheckboxSwitch } from '@prisjakt/ui/checkbox/variants';
import { IconTrash } from '@prisjakt/ui/icon';
import { BodySmallText, FontEmphasis } from '@prisjakt/ui/text/variants';

import { isValidEmail } from 'src/areas/account/helpers';
import {
  removeAlertError,
  removeAlertRequest,
} from 'src/areas/sidebar/actions/alert';
import { CtxLink } from 'src/components/render-context/ctx-link';
import { MultiRadio, Variants } from 'src/components/review-form';
import { useIsAuthenticated } from 'src/hooks/use-is-authenticated';

import { AppState } from 'src/reducers';

import { Maybe, UserReviewSummary } from 'src/types';
import { ProductAlertFormData } from '../../types';

import {
  AlertForm,
  CancelButton,
  CheckboxNotify,
  EmailHint,
  EmailInput,
  NumberInput,
  PriceBelowWrapper,
  PriceStepInput,
  PriceStepWrapper,
  RatingWrapper,
  RemoveButton,
  SaveAlertButton,
  ShopsFilterWrapper,
  SubmitButtonWrapper,
  TextInput,
} from './styles';

const getDefaultPriceThreshold = (
  regularPrice: number | null | undefined,
  minimumStepValue: number = 0.1,
  numberOfDecimals: number = 2
): string =>
  regularPrice
    ? (regularPrice - minimumStepValue).toFixed(numberOfDecimals)
    : '0';

const getDefaultPriceStep = (
  regularPrice: number | null | undefined, // Can this come from like the server??
  minimumStepValue: number = 0.1,
  numberOfDecimals: number = 2
): string => {
  const minDefaultPriceStep = minimumStepValue * 10;

  // Returns the largest value of 1% of regularPrice or minDefaultPriceStep
  return regularPrice
    ? Math.max(regularPrice * 0.01, minDefaultPriceStep).toFixed(
        numberOfDecimals
      )
    : String(minDefaultPriceStep);
};

type StoreQuery = {
  store?: Maybe<{
    userReviewSummary?: Maybe<UserReviewSummary>;
  }>;
};

const numberOfStoresAboveRating = (
  prices: Maybe<StoreQuery>[] | null | undefined,
  rating: number
): number => {
  if (!prices) {
    return -1;
  }

  return prices.filter(
    price => Number(price?.store?.userReviewSummary?.rating) > rating
  ).length;
};

export enum AlertTypes {
  IN_STOCK = 'IN_STOCK',
  PRICE_DROPS = 'PRICE_DROPS',
  IN_STOCK_PRICE_DROPS = 'IN_STOCK_PRICE_DROPS',
}

export enum Errors {
  ERROR_EMAIL_INVALID = 'error.email.invalid',
  ERROR_EMAIL_IN_USE = 'alert.email-associated-with-user',
  ERROR_UNKNOWN = 'alert.error',
  ERROR_THRESHOLD_TOO_LOW = 'alert.threshold-too-low',
}

export type ProductAlert = {
  alertId?: number;
  productId: number;
  email?: string;
  priceStep?: number;
  storeGradeThreshold?: number;
  type: AlertTypes;
  priceThreshold?: number;
};

type OnCreate = (alert: ProductAlert) => void;

interface ProductAlertFormProps {
  productAlertFormData: ProductAlertFormData;
  onClose?: () => void;
  onCreate: OnCreate; // Remove? Should be alert type export type AlertCreateOptions = {
  privacyPolicyUri?: string;
  error?: string | boolean;
  isEdit?: boolean;
  shouldAutoFocus?: boolean;
  disabled?: boolean;
  showRemoveButton?: boolean;
}

export const ProductAlertForm = memo((props: ProductAlertFormProps) => {
  const {
    productAlertFormData,
    onClose,
    onCreate,
    privacyPolicyUri,
    error,
    isEdit = true,
    shouldAutoFocus,
    disabled,
    showRemoveButton = true,
  } = props;

  const { market, t } = useAppContext();

  const product = productAlertFormData.product;

  const userProductAlerts: {
    productId: number;
    alertId: number;
  }[] = useSelector((state: AppState) => state.areas.sidebar.alerts.products);

  const dispatch = useDispatch();

  const emailInput = useRef<HTMLInputElement>(null);

  const isAuthenticated = useIsAuthenticated();
  const { minimumStepValue, numberOfDecimals } = CurrencySettings[market.code];

  const [errors, setErrors] = useState<Set<Errors>>(new Set());
  const [email, setEmail] = useState<string>('');
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [chosenStoreGradeThreshold, setChosenStoreGradeThreshold] = useState<
    number
  >(product?.alert?.storeGradeThreshold ?? 0);

  const [priceThreshold, setPriceThreshold] = useState<string>(() => {
    return product?.alert?.priceThreshold
      ? String(product.alert.priceThreshold)
      : getDefaultPriceThreshold(
          product?.priceSummary.regular,
          minimumStepValue,
          numberOfDecimals
        );
  });
  const [onlyAlertIfInStock, setOnlyAlertIfInStock] = useState<boolean>(
    product?.alert?.alertType !== AlertTypes.PRICE_DROPS
  );

  // Default to true
  const [priceStepCheckbox, setPriceStepCheckbox] = useState<boolean>(
    product?.alert ? !!product.alert.priceStep : true
  );

  const [priceStep, setPriceStep] = useState<string>(() => {
    if (product?.alert?.priceStep) {
      return String(product.alert.priceStep);
    }

    return getDefaultPriceStep(
      product?.priceSummary.regular,
      minimumStepValue,
      numberOfDecimals
    );
  });

  useEffect(() => {
    if (emailInput.current && shouldAutoFocus) {
      emailInput.current.focus();
    }

    return function cleanup() {
      dispatch(removeAlertError(undefined));
    };
  }, [dispatch, shouldAutoFocus]);

  const clearErrors = (): void => setErrors(new Set());

  const addError = (newError: Errors) => {
    setErrors(prevErrors => {
      const draftErrors = new Set<Errors>(prevErrors);
      return draftErrors.add(newError);
    });
  };

  const removeError = (errorToRemove: Errors) => {
    setErrors(prevErrors => {
      const draftErrors = new Set<Errors>(prevErrors);
      draftErrors.delete(errorToRemove);
      return draftErrors;
    });
  };

  useEffect(() => {
    if (!error) {
      return;
    }

    switch (error) {
      case Errors.ERROR_EMAIL_INVALID:
      case Errors.ERROR_EMAIL_IN_USE: {
        addError(error);
        break;
      }
      default: {
        addError(Errors.ERROR_UNKNOWN);
      }
    }
  }, [error]);

  const handlePriceThresholdChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPriceThreshold(event.target.value);

    if (!event.target.value || Number(event.target.value) < 0) {
      addError(Errors.ERROR_THRESHOLD_TOO_LOW);
    } else {
      removeError(Errors.ERROR_THRESHOLD_TOO_LOW);
    }
  };

  const handlePriceStepChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPriceStep(event.target.value);
  };

  const handleOnlyAlertIfInStockChange = () =>
    setOnlyAlertIfInStock(state => !state);

  const handlePriceStepCheckboxChange = () =>
    setPriceStepCheckbox(state => !state);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    /* The error state is duplicated, between Redux and local state. This is due
    to the fact that we are using sagas to make the create alert request.
    The problem, apart from duplication, is that the error messages outlive the
    form since they are stored in redux. So the error handling in this form
    needs to be improved/simplified. But for now the code below is required to
    fix the issue with the email error messages not disappearing even though the
    user corrects the email.
    */
    if (isValidEmail(event.target.value)) {
      removeError(Errors.ERROR_EMAIL_INVALID);
    } else if (hasSubmitted) {
      addError(Errors.ERROR_EMAIL_INVALID);
    }
    dispatch(removeAlertError(undefined));
    removeError(Errors.ERROR_EMAIL_IN_USE);
    setEmail(event.target.value);
  };

  const saveAlert = useCallback(
    (event: React.FormEvent): void => {
      event.preventDefault();
      setHasSubmitted(true);

      const { rating = 0 } = serialize(event.target, {
        hash: true,
      });

      const userAlert = userProductAlerts.find(
        alert => alert.productId === product?.id
      );

      if (!product) {
        return;
      }

      clearErrors();

      if (!isAuthenticated && !isValidEmail(email)) {
        addError(Errors.ERROR_EMAIL_INVALID);
        if (shouldAutoFocus && emailInput.current) {
          emailInput.current.focus();
          emailInput.current.select();
        }
        return;
      }

      if (Number(priceThreshold) < 0 || Number(priceStep) < 0) {
        return;
      }

      const graphQLAlert = {
        type: onlyAlertIfInStock
          ? AlertTypes.IN_STOCK_PRICE_DROPS
          : AlertTypes.PRICE_DROPS,
        priceThreshold: Number(priceThreshold),
      };
      const priceStepValue = priceStepCheckbox ? Number(priceStep) : undefined;

      onCreate({
        alertId: userAlert && userAlert.alertId,
        productId: product.id,
        email: !isAuthenticated ? email : undefined,
        priceStep: priceStepValue,
        storeGradeThreshold: rating ? Number(rating) * 2 : undefined,
        ...graphQLAlert,
      });
    },
    [
      email,
      onCreate,
      onlyAlertIfInStock,
      priceStep,
      priceStepCheckbox,
      priceThreshold,
      product,
      shouldAutoFocus,
      userProductAlerts,
      isAuthenticated,
    ]
  );

  const removeAlert = () => {
    const userAlert = userProductAlerts.find(
      alert => alert.productId === product?.id
    );

    if (!userAlert?.alertId) return;

    dispatch(removeAlertRequest(userAlert.alertId, product?.id));
    if (onClose) onClose();
  };

  const renderTypeOptions = () => {
    return (
      <>
        <div>
          <PriceBelowWrapper>
            <BodySmallText emphasis={FontEmphasis.MEDIUM}>
              {t('alerts', 'type.price-below')}
            </BodySmallText>
            <NumberInput
              name="priceThreshold"
              ariaLabel={t('alerts', 'alert-form.price-limit')}
              warning={errors.has(Errors.ERROR_THRESHOLD_TOO_LOW)}
              value={priceThreshold}
              onChange={handlePriceThresholdChange}
              step={minimumStepValue}
              max={
                product?.priceSummary.regular
                  ? Number(
                      (product.priceSummary.regular - minimumStepValue).toFixed(
                        numberOfDecimals
                      )
                    )
                  : undefined
              }
              min={0}
              data-testid="price-threshold-number-input"
            />
          </PriceBelowWrapper>

          <PriceStepWrapper>
            <CheckboxSwitch
              text={t('alerts', 'alert-form.price-step-checkbox')}
              onChange={handlePriceStepCheckboxChange}
              checked={priceStepCheckbox}
            />
            <PriceStepInput>
              <NumberInput
                name="priceStep"
                ariaLabel={t('alerts', 'alert-form.price-interval')}
                data-testid="price-step-number-input"
                disabled={!priceStepCheckbox}
                warning={Number(priceStep) < 0}
                value={priceStep}
                onChange={handlePriceStepChange}
                step={minimumStepValue}
                max={
                  product?.priceSummary.regular
                    ? Number(
                        (
                          product.priceSummary.regular - minimumStepValue
                        ).toFixed(numberOfDecimals)
                      )
                    : undefined
                }
                min={0}
              />
            </PriceStepInput>
          </PriceStepWrapper>
        </div>

        <CheckboxNotify
          name="onlyAlertIfInStock"
          checked={onlyAlertIfInStock}
          text={t('alerts', 'alert-form.only-notify-in-stock')}
          onChange={handleOnlyAlertIfInStockChange}
        />

        <ShopsFilterWrapper>
          <BodySmallText emphasis={FontEmphasis.MEDIUM}>
            {t('alerts', 'alert-form.shops-with-higher-rating')}
          </BodySmallText>
          <RatingWrapper>
            <MultiRadio
              currentValue={
                product?.alert?.storeGradeThreshold
                  ? product?.alert?.storeGradeThreshold / 2
                  : null
              }
              disabledOptions={[5]}
              name="rating"
              onClick={e => {
                setChosenStoreGradeThreshold(Number(e.target.value) * 2);
              }}
              options={[1, 2, 3, 4, 5]}
              optionValues={[
                t('reviews', 'rating.terrible'),
                t('reviews', 'rating.bad'),
                t('reviews', 'rating.ok'),
                t('reviews', 'rating.good'),
                t('reviews', 'rating.excellent'),
              ]}
              required
              size="small"
              variant={Variants.rate}
            />
            <p>
              {product?.prices
                ? t('alerts', 'alert-form.n-stores', {
                    n: numberOfStoresAboveRating(
                      product?.prices?.nodes,
                      chosenStoreGradeThreshold
                    ),
                  })
                : null}
            </p>
          </RatingWrapper>
        </ShopsFilterWrapper>
      </>
    );
  };

  return (
    <AlertForm method="post" onSubmit={saveAlert} noValidate>
      {renderTypeOptions()}
      <EmailInput error={errors.has(Errors.ERROR_EMAIL_INVALID)}>
        {!isAuthenticated && (
          <>
            <TextInput
              type="email"
              name="email"
              inputRef={emailInput}
              defaultValue={email}
              onChange={handleEmailChange}
              placeholder={t('alerts', 'alert-form.email-placeholder')}
              tabIndex={0}
              autoFocus={shouldAutoFocus}
              // Do not send this element to CUX (cux.io)
              data-cux-exclude
            />
            <EmailHint visible={errors.has(Errors.ERROR_EMAIL_IN_USE)}>
              <T domain="alerts">alert-form.email-in-use-error</T>
              &nbsp;
              <CtxLink to="/login">
                <T domain="alerts">alert-form.login-error</T>
              </CtxLink>
            </EmailHint>
            <EmailHint error visible={errors.has(Errors.ERROR_EMAIL_INVALID)}>
              <T domain="alerts">alert-form.invalid-email-error</T>
            </EmailHint>
            <EmailHint error visible={errors.has(Errors.ERROR_UNKNOWN)}>
              <T domain="alerts">alert-form.unknown-error</T>
            </EmailHint>
            {privacyPolicyUri && (
              <EmailHint visible discreetLink>
                <CtxLink to={privacyPolicyUri}>
                  <T domain="product">alert.how-your-email-is-used</T>
                </CtxLink>
              </EmailHint>
            )}
          </>
        )}
      </EmailInput>

      {isEdit && showRemoveButton && (
        <RemoveButton onClick={removeAlert}>
          <ButtonIcon>
            <IconTrash />
          </ButtonIcon>
          <ButtonText>{t('product', 'alert.delete-button')}</ButtonText>
        </RemoveButton>
      )}

      <SubmitButtonWrapper>
        {isEdit && onClose && product && (
          <CancelButton onClick={onClose}>
            {t('alerts', 'alert-form.cancel-button')}
          </CancelButton>
        )}

        <SaveAlertButton
          disabled={disabled || errors.size > 0}
          containerProps={{
            'data-testid': 'CreateAlertButton',
            'data-test': 'CreateAlertButton',
          }}
          htmlType="submit"
          fullWidth
        >
          {t('alerts', 'alert-form.create-button')}
        </SaveAlertButton>
      </SubmitButtonWrapper>
    </AlertForm>
  );
});
